import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Link,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Center,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {useState, useEffect, useCallback, useMemo} from "react";
import {FiPhone} from "react-icons/fi";
import {lightFormat} from "date-fns";
import {fetchRescheduleOptions} from "../../crudRequests";

function formatSlotTime(timestamp) {
  const d = new Date(timestamp);
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, "0")} ${period}`;
}

export default function RescheduleAppointmentModal({
  isOpen,
  onClose,
  appointment,
  canModify,
  blockedMessage,
  clinicPhone,
  onConfirm,
  isLoading,
  state,
  // Changes when the server has told us our options are stale; refetching is
  // the only honest response to a 409.
  reloadKey = 0,
}) {
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [availabilityData, setAvailabilityData] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState("");

  const minDate = lightFormat(
    new Date(Date.now() + 24 * 60 * 60 * 1000),
    "yyyy-MM-dd"
  );

  // Build list of eligible clinicians for this service + location
  const eligibleClinicians = useMemo(() => {
    if (!appointment || !state?.doctors) return [];
    const serviceId = appointment.serviceId;
    const locationId = appointment.lid;

    return state.doctors.filter((doc) => {
      const hasService =
        Array.isArray(doc.services) && doc.services.includes(serviceId);
      const hasLocation = Array.isArray(doc.lid)
        ? doc.lid.includes(locationId)
        : doc.lid === locationId;
      return hasService && hasLocation;
    });
  }, [state?.doctors, appointment]);

  // Sort: current clinician first, then alphabetical
  const sortedClinicians = useMemo(() => {
    if (!appointment) return eligibleClinicians;
    return [...eligibleClinicians].sort((a, b) => {
      if (a.did === appointment.did) return -1;
      if (b.did === appointment.did) return 1;
      const nameA = `${a.lastName || ""} ${a.firstName || a.name || ""}`;
      const nameB = `${b.lastName || ""} ${b.firstName || b.name || ""}`;
      return nameA.localeCompare(nameB);
    });
  }, [eligibleClinicians, appointment]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && appointment) {
      const currentDoc = state?.doctors?.find(
        (d) => d.did === appointment.did
      );
      setSelectedClinician(
        currentDoc
          ? {did: currentDoc.did, name: `${currentDoc.firstName || currentDoc.name || ""} ${currentDoc.lastName || ""}`.trim()}
          : {did: appointment.did, name: appointment.dName || ""}
      );
      setSelectedDate(
        appointment.ISOdate
          ? lightFormat(new Date(appointment.ISOdate), "yyyy-MM-dd")
          : ""
      );
      setAvailabilityData({});
      setSelectedSlot(null);
      setError("");
    }
  }, [isOpen, appointment, state?.doctors]);

  // Derive month/year from selectedDate so we only re-fetch when month changes
  const selectedMonth = selectedDate
    ? new Date(selectedDate + "T00:00:00").getMonth()
    : null;
  const selectedYear = selectedDate
    ? new Date(selectedDate + "T00:00:00").getFullYear()
    : null;

  // Fetch options when clinician or month/year changes.
  //
  // The engine answers a RANGE and keys its days in the clinic's timezone,
  // which this browser does not know. Asking a day either side of the month
  // covers it whatever that timezone turns out to be; the calendar reads one
  // day at a time and ignores the rest.
  const fetchAvailability = useCallback(
    async (clinicianDid, month, year) => {
      if (!clinicianDid || month == null || year == null || !appointment) return;

      setLoadingSlots(true);
      setError("");
      try {
        const from = new Date(year, month, 0, 0, 0, 0, 0).getTime();
        const to = new Date(year, month + 1, 1, 23, 59, 59, 999).getTime();
        const response = await fetchRescheduleOptions(state, {
          appointmentId: appointment.aid || appointment.id,
          clinicianDid,
          from,
          to,
        });
        setAvailabilityData(response.data?.days || {});
      } catch (err) {
        console.error("Error fetching availability:", err);
        setError("Failed to load availability. Please try again.");
        setAvailabilityData({});
      } finally {
        setLoadingSlots(false);
      }
    },
    [state?.jwt, state?.db, appointment?.aid, appointment?.id]
  );

  // Re-fetch when clinician or selected month/year changes (not on day-only changes)
  useEffect(() => {
    if (isOpen && selectedClinician?.did && selectedMonth != null && selectedYear != null) {
      setSelectedSlot(null);
      fetchAvailability(selectedClinician.did, selectedMonth, selectedYear);
    }
  }, [isOpen, selectedClinician?.did, selectedMonth, selectedYear, fetchAvailability, reloadKey]);

  // Available slots for the selected date
  const slotsForDate = useMemo(() => {
    if (!selectedDate || !availabilityData) return [];
    const dayData = availabilityData[selectedDate];
    if (!dayData?.slots) return [];
    // Filter to only slots for the selected clinician and sort by start time
    return dayData.slots
      .filter((s) => s.did === selectedClinician?.did)
      .sort((a, b) => a.start - b.start);
  }, [availabilityData, selectedDate, selectedClinician?.did]);

  const handleClinicianChange = (e) => {
    const did = e.target.value;
    const doc = state.doctors?.find((d) => d.did === did);
    if (doc) {
      setSelectedClinician({
        did: doc.did,
        name: `${doc.firstName || doc.name || ""} ${doc.lastName || ""}`.trim(),
      });
    }
    setSelectedSlot(null);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedSlot(null);
  };

  const handleClose = () => {
    setSelectedDate("");
    setSelectedClinician(null);
    setSelectedSlot(null);
    setAvailabilityData({});
    setError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedDate || !selectedSlot) {
      setError("Please select a date and time slot.");
      return;
    }

    const startAt = selectedSlot.start;
    const endAt = selectedSlot.end;
    const dateObj = new Date(startAt);
    const formattedTime = formatSlotTime(startAt);

    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const formattedDate = `${weekdays[dateObj.getDay()]}, ${months[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;

    const durationMs = endAt - startAt;
    const durationMin = Math.round(durationMs / 60000);
    const durationStr = `${durationMin} min`;

    // Status flags are the server's to decide: it drops the previous
    // confirmation, stamps the previous start time, and notifies.
    const updateData = {
      date: formattedDate,
      time: formattedTime,
      duration: durationStr,
      ISOdate: startAt,
      startAt: startAt,
      endAt: endAt,
    };

    // Include clinician change if different from original
    if (selectedClinician && selectedClinician.did !== appointment.did) {
      const doc = state.doctors?.find(
        (d) => d.did === selectedClinician.did
      );
      updateData.did = selectedClinician.did;
      updateData.dName = selectedClinician.name;
      if (doc) {
        updateData.clinicianFirstName = doc.firstName || doc.name || "";
        updateData.clinicianLastName = doc.lastName || "";
        if (doc.email) updateData.dEmail = doc.email;
      }
    }

    await onConfirm(updateData);
  };

  if (!canModify) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Unable to Reschedule Online</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert
              status="warning"
              borderRadius="md"
              flexDirection="column"
              alignItems="start"
            >
              <AlertIcon />
              <AlertTitle mt={2}>Time Restriction</AlertTitle>
              <AlertDescription mt={2}>{blockedMessage}</AlertDescription>
            </Alert>
            {clinicPhone && (
              <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                <Text fontWeight="semibold" mb={2}>
                  Contact the clinic:
                </Text>
                <Link
                  href={`tel:${clinicPhone}`}
                  color="blue.500"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <FiPhone />
                  {clinicPhone}
                </Link>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reschedule Appointment</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Current appointment info */}
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="semibold">
                  {appointment?.service?.name || appointment?.service}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Current: {appointment?.date} at {appointment?.time}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Clinician: {appointment?.dName || "N/A"}
                </Text>
              </Box>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {/* Clinician selector */}
              {sortedClinicians.length > 1 && (
                <FormControl>
                  <FormLabel>Clinician</FormLabel>
                  <Select
                    value={selectedClinician?.did || ""}
                    onChange={handleClinicianChange}
                  >
                    {sortedClinicians.map((doc) => (
                      <option key={doc.did} value={doc.did}>
                        Clinician {doc.firstName || doc.name || ""}{" "}
                        {doc.lastName || ""}
                        {doc.did === appointment?.did ? " (current)" : ""}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Date picker */}
              <FormControl isRequired>
                <FormLabel>New Date</FormLabel>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={minDate}
                />
              </FormControl>

              {/* Available time slots */}
              {loadingSlots && (
                <Center py={4}>
                  <Spinner size="sm" mr={2} />
                  <Text fontSize="sm" color="gray.500">
                    Loading availability...
                  </Text>
                </Center>
              )}

              {!loadingSlots && selectedDate && slotsForDate.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Available Times
                  </Text>
                  <Wrap spacing={2}>
                    {slotsForDate.map((slot) => {
                      const isSelected =
                        selectedSlot?.start === slot.start &&
                        selectedSlot?.end === slot.end;
                      return (
                        <WrapItem key={`${slot.start}-${slot.end}`}>
                          <Button
                            size="sm"
                            variant={isSelected ? "solid" : "outline"}
                            colorScheme={isSelected ? "blue" : "gray"}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {formatSlotTime(slot.start)}
                          </Button>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              )}

              {!loadingSlots && selectedDate && slotsForDate.length === 0 && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  No available times for this date. Please select another date.
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isLoading}
              isDisabled={!selectedDate || !selectedSlot}
            >
              Reschedule Appointment
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
